import {
  PairedCostRouteItemWithQuery,
  schAlertType,
  schPairedCostRouteItem,
  schQueryParams,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import {
  LocationType,
  getLocationTypeProximityRequirementMiles,
} from '../../../../utils/locationTypeProximityRequirementMiles.ts';
import { proximityMiles } from '../../../../utils/proximityMiles.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getCompTypeLabel, getScopedAlertType } from '../utils/compTypeScoping.ts';

const MIN_PROXIMATE_COMPS = 2;

export const insufficientProximateComps: AlertDefinition = ({
  compSequence,
  compType,
  propertyDataEntities,
  order,
}) => {
  const subject = propertyDataEntities.subject as SubjectPropertyDataState | undefined;
  const locationType = subject?.subjectData?.editableData?.propertyIdentification?.locationType as
    | LocationType
    | undefined;
  const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
  const subjectCoords = subject?.subjectData?.editableData?.propertyIdentification?.coordinates;

  if (!locationType || proximityRequirementMiles === undefined || !subjectCoords) return [];

  const proximateCompCount = compSequence.filter((id) => {
    const comp = propertyDataEntities[id] as CompPropertyDataState | undefined;
    const coords = comp?.compData.editableData.propertyIdentification.coordinates;
    if (!coords) return false;

    const miles = proximityMiles(subjectCoords, coords);
    return miles !== undefined && miles <= proximityRequirementMiles;
  }).length;

  if (proximateCompCount >= MIN_PROXIMATE_COMPS) return [];

  let message: string;
  switch (compType) {
    case 'aiv':
    case 'arv':
      message = `Fewer than 2 ${getCompTypeLabel(compType)} Comps are within the expected proximity threshold.`;
      break;
    default:
      message = 'Fewer than 2 Comps are within the expected proximity threshold.';
      break;
  }

  const fixRoute =
    `${schPairedCostRouteItem.enum.features}?${schQueryParams.enum.addComps}=true` as PairedCostRouteItemWithQuery;

  return [
    {
      type: getScopedAlertType(schAlertType.enum.insufficientProximateComps, compType),
      status: 'active',
      severity: 'error',
      orderType: ['appraisify'],
      message,
      ...(compType ? { compType } : {}),
      fixingInfo: {
        tips: 'If closer alternatives are unavailable, provide market-supported reasoning—such as limited inventory, a rural location, or a unique property type.',
        pairedCostRouteItem: fixRoute,
        fixingMethod: ['comment', 'action'],
        commentAlwaysRequired: false,
      },
      triggers: getAlertTriggers({ fixRoute, order }),
    },
  ];
};
